/**
 * Reading tool input the way models actually send it, and the shared steps
 * every tool takes with it: resolving an object reference, stating a refusal,
 * recording a result.
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { resolveObjectRef } from '../authoring/resolveObjectRef';
import type { TransformEntry } from '../authoring/transformOps';
import { UNDO_IN_PROGRESS_REFUSAL, type Refusal } from '../authoring/types';
import type { UndoCommand } from '../undo/UndoCommand';
import { getUndoManager } from '../undo/UndoManager';
import type { SceneTool, SceneToolHost, ToolInputSchema, ToolOutcome } from './types';

export type Vec3 = [number, number, number];

/** An `[x, y, z]` field's schema. */
export const VEC3 = { type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3 } as const;

export type ToolInput = Record<string, unknown>;

/** `n` to `places` decimals, with no -0. */
export const round = (n: number, places: number): number => Math.round(n * 10 ** places) / 10 ** places + 0;

/** Tool input is untyped at runtime: anything but an object reads as no fields. */
export function asInput(input: unknown): ToolInput {
    return input !== null && typeof input === 'object' && !Array.isArray(input) ? input as ToolInput : {};
}

export function fail(text: string): ToolOutcome {
    return { text, isError: true };
}

/**
 * A reference that names several objects, or a deleted one. A tool that meets
 * one answers `Error: <message>`, so the model sees the candidate paths and
 * retries with one.
 */
export class ObjectRefError extends Error {
    override name = 'ObjectRefError';
}

/**
 * The object a tool's reference names: `"selected"`, a path or a name, by
 * `resolveObjectRef`. Null when nothing matches, so each tool keeps its own
 * not-found answer; an ambiguous or deleted match throws {@link ObjectRefError}.
 */
export function resolveRef(host: SceneToolHost, ref: unknown): SceneObject | null {
    if (typeof ref !== 'string') return null;
    if (ref === 'selected') return host.selected?.() ?? null;
    const result = resolveObjectRef(host.scene, ref);
    if ('object' in result) return result.object;
    if (result.error === 'NOT_FOUND') return null;
    throw new ObjectRefError(result.message);
}

/** {@link resolveRef} for one batch entry, which reports on its own line: the object, or that line's verdict. */
export function resolveEntry(host: SceneToolHost, ref: unknown): SceneObject | string {
    try {
        return resolveRef(host, ref) ?? 'NOT FOUND';
    } catch (error) {
        if (error instanceof ObjectRefError) return `ERROR ${error.message}`;
        throw error;
    }
}

/** A refusal as a sentence for the model, in the host's words when it has its own. */
export function refusalText(host: SceneToolHost, refusal: Refusal): string {
    if (host.refusalText) return host.refusalText(refusal);
    return /[.!?]$/.test(refusal.message) ? refusal.message : `${refusal.message}.`;
}

/** Keep an operation's step and report what its reference copy left behind. */
export function keep(host: SceneToolHost, tool: string, result: { command: UndoCommand | null; orphaned?: readonly string[] }): void {
    if (result.command) host.record(result.command);
    if (result.orphaned && result.orphaned.length > 0) host.orphaned?.(tool, result.orphaned);
}

// ── Vectors ──────────────────────────────────────────────────────────────────

/** Undefined, null and `[]` mean "leave it alone": models send `[]` for vectors they don't mean to change. */
function isAbsent(value: unknown): boolean {
    return value === undefined || value === null || (Array.isArray(value) && value.length === 0);
}

/**
 * An `[x, y, z]` field: unset when absent (see `isAbsent`), an error the model
 * can act on when malformed, never a partly-applied vector.
 */
export function readVec3(input: ToolInput, field: string): { value?: Vec3; error?: string } {
    const raw = input[field];
    if (isAbsent(raw)) return {};
    if (!Array.isArray(raw) || raw.length !== 3 || !raw.every(n => typeof n === 'number' && Number.isFinite(n))) {
        return { error: `Error: ${field} must be [x, y, z] with three numbers (got ${JSON.stringify(raw)}). Omit it to leave it unchanged.` };
    }
    return { value: [raw[0], raw[1], raw[2]] as Vec3 };
}

export interface TransformArgs {
    position?: Vec3;
    rotation?: Vec3;
    scale?: Vec3;
}

/** Position, rotation (Euler degrees) and scale; the first malformed field's error, so a bad one never rides in behind a good one. */
export function readTransformArgs(input: ToolInput): TransformArgs | { error: string } {
    const out: TransformArgs = {};
    for (const field of ['position', 'rotation', 'scale'] as const) {
        const read = readVec3(input, field);
        if (read.error) return { error: read.error };
        if (read.value) out[field] = read.value;
    }
    return out;
}

// ── Colors ───────────────────────────────────────────────────────────────────

/** A hex color as `#RRGGBBAA` (`#RRGGBB` gets opaque alpha), or null when it is neither. */
export function readHexColor(color: unknown): string | null {
    if (typeof color !== 'string') return null;
    const hex = color.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return `${hex}FF`;
    return /^#[0-9A-Fa-f]{8}$/.test(hex) ? hex : null;
}

/** Why `material_index` can't name a slot, or null when it can. */
export function materialIndexError(materialIndex: unknown): string | null {
    return Number.isInteger(materialIndex) && (materialIndex as number) >= 0
        ? null
        : `material_index must be a whole number, 0 or more (got ${JSON.stringify(materialIndex)})`;
}

// ── Batches ──────────────────────────────────────────────────────────────────

export interface BatchEntry {
    object: string;
    /** The top-level fields, with the entry's own fields over them. */
    fields: ToolInput;
}

/**
 * A call's `objects`: object names, which take the call's top-level value, or
 * `{object, …}` entries with their own; an entry's `name` stands for `object`.
 * Returns the error text when there is no such list.
 */
export function readBatchEntries(input: ToolInput): BatchEntry[] | string {
    const list: unknown[] | null = Array.isArray(input.objects) ? input.objects : null;
    if (!list || list.length === 0) {
        return 'Error: Provide `objects`: object names, which share the call\'s value, or {object, …} entries, one value each.';
    }
    const out: BatchEntry[] = [];
    for (const raw of list) {
        if (typeof raw === 'string') {
            out.push({ object: raw, fields: input });
            continue;
        }
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
            const entry = raw as ToolInput;
            const name = entry.object ?? entry.name;
            if (typeof name !== 'string') {
                return `Error: batch entry ${JSON.stringify(entry).slice(0, 80)} has no "object" name.`;
            }
            out.push({ object: name, fields: { ...input, ...entry } });
            continue;
        }
        return `Error: batch entries must be object names or {object, …} entries, got ${typeof raw}.`;
    }
    return out;
}

// ── Mates and relative placement ─────────────────────────────────────────────

/** `mate`: which anchor of the moved object joins which anchor of which object, as the operation takes it; or what is wrong with it. */
export function readMate(input: unknown, resolve: (ref: string) => SceneObject | null): string | NonNullable<TransformEntry['mate']> {
    const mate = input as { anchor?: unknown; to?: unknown; to_anchor?: unknown; spin?: unknown } | null;
    if (!mate || typeof mate !== 'object' || typeof mate.anchor !== 'string' || typeof mate.to !== 'string' || typeof mate.to_anchor !== 'string'
        || (mate.spin !== undefined && (typeof mate.spin !== 'number' || !Number.isFinite(mate.spin)))) {
        return 'mate needs {anchor, to, to_anchor, spin?}: the moved object\'s anchor name, the object it joins, that object\'s anchor name, and an optional spin in degrees.';
    }
    const to = resolve(mate.to);
    if (!to) return `mate target "${mate.to}" was not found.`;
    return { anchor: mate.anchor, to, toAnchor: mate.to_anchor, spin: mate.spin };
}

export const POSITION_ANCHORS = ['above', 'below', 'in_front', 'behind', 'left', 'right', 'center'] as const;
export type PositionAnchor = typeof POSITION_ANCHORS[number];

/**
 * The world axis each anchor points along. The scene is right-handed and Y-up
 * and the default camera looks from +Z, so +Z faces the viewer and +X is
 * screen right.
 */
const ANCHOR_AXIS: Readonly<Record<PositionAnchor, Vec3>> = {
    above: [0, 1, 0],
    below: [0, -1, 0],
    in_front: [0, 0, 1],
    behind: [0, 0, -1],
    left: [-1, 0, 0],
    right: [1, 0, 0],
    center: [0, 0, 0],
};

/** `world` in the frame of `parent` (a node), or unchanged with none: the local position that puts something there. */
export function worldToLocal(world: Vector3, parent: TransformNode | null): Vec3 {
    if (!parent) return [world.x, world.y, world.z];
    parent.computeWorldMatrix(true);
    const local = Vector3.TransformCoordinates(world, parent.getWorldMatrix().clone().invert());
    return [local.x, local.y, local.z];
}

/**
 * A `parent`-local position against a face of `target`'s world bounds: on the
 * anchor's axis, against that face and `offset` further out; on the other two,
 * centred. Null when the target draws nothing. It places an origin, so it is
 * where an object goes before it exists; {@link restAgainst} then rests it by
 * its own bounds.
 */
export function positionAgainst(target: SceneObject, anchor: PositionAnchor, offset: number, parent: SceneObject | null): Vec3 | null {
    target.transform.computeWorldMatrix(true);
    const { min, max } = target.transform.getHierarchyBoundingVectors(true);
    if (!Number.isFinite(min.x) || !Number.isFinite(max.x)) return null;
    const axis = ANCHOR_AXIS[anchor];
    const along = (dir: number, lo: number, hi: number): number => dir === 0 ? (lo + hi) / 2 : dir > 0 ? hi + offset : lo - offset;
    const world = new Vector3(along(axis[0], min.x, max.x), along(axis[1], min.y, max.y), along(axis[2], min.z, max.z));
    return worldToLocal(world, parent?.transform ?? null);
}

/** World bounds of what draws under `object` (scale-0 meshes left out), or null when nothing does. */
export function drawnBounds(object: SceneObject): { min: Vector3; max: Vector3 } | null {
    object.transform.computeWorldMatrix(true);
    const { min, max } = object.transform.getHierarchyBoundingVectors(true, mesh => {
        if (typeof mesh.getBoundingInfo !== 'function') return true;
        const box = mesh.getBoundingInfo().boundingBox;
        return !box.minimumWorld.equals(box.maximumWorld);
    });
    return Number.isFinite(min.x) && Number.isFinite(max.x) && max.x >= min.x ? { min, max } : null;
}

/**
 * What draws under `object` (the meshes `drawnBounds` counts), boxed in its
 * parent's frame like its position, rounded to 3 decimals: a build snapshot's
 * `bounds`. Each mesh's own box is turned into that frame, so a turned parent
 * doesn't loosen it. Undefined when nothing draws.
 */
export function boundsInParent(object: SceneObject): { min: Vec3; max: Vec3; center: Vec3 } | undefined {
    const parent = object.transform.parent as TransformNode | null;
    const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
    for (const node of [object.transform, ...object.transform.getDescendants(false)]) {
        const mesh = node as AbstractMesh;
        if (typeof mesh.getBoundingInfo !== 'function' || !mesh.getTotalVertices()) continue;
        mesh.computeWorldMatrix(true);
        const box = mesh.getBoundingInfo().boundingBox;
        if (box.minimumWorld.equals(box.maximumWorld)) continue;
        for (const corner of box.vectorsWorld) {
            worldToLocal(corner, parent).forEach((v, a) => { min[a] = Math.min(min[a], v); max[a] = Math.max(max[a], v); });
        }
    }
    if (!Number.isFinite(min[0])) return undefined;
    const r = (v: number[]): Vec3 => [round(v[0], 3), round(v[1], 3), round(v[2], 3)];
    return { min: r(min), max: r(max), center: r(min.map((v, a) => (v + max[a]) / 2)) };
}

/** Where `object` ended up, for a result: " Now at [x, y, z], size [w, h, d] (world)." or "" when nothing draws. */
export function whereText(object: SceneObject): string {
    const bounds = drawnBounds(object);
    if (!bounds) return '';
    const { min, max } = bounds;
    const r = (v: number[]): string => `[${v.map(n => round(n, 3)).join(', ')}]`;
    // Below the ground is rarely meant: an origin-centred primitive given y = 0 sinks halfway.
    let sunk = '';
    if (min.y < -0.005) {
        // The fix is a position, which is in the parent's frame: lift the origin by the depth, then say it locally.
        const lifted = worldToLocal(object.transform.getAbsolutePosition().add(new Vector3(0, -min.y, 0)), object.transform.parent as TransformNode | null);
        sunk = ` Its bottom is at y=${round(min.y, 3)}, below the ground: set its position to [${lifted.map(n => round(n, 3)).join(', ')}] to stand it on the ground.`;
    }
    return ` Now at ${r([(min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2])}, size ${r([max.x - min.x, max.y - min.y, max.z - min.z])} (world).${sunk}`;
}

/**
 * The parent-local position that puts `object` against `target`'s face for
 * `anchor`: its near side touching that face, `offset` metres away, and
 * centred on the target across the face ("above" rests it on top). `center`
 * centres it on the target. Null when either has no bounds.
 */
export function restAgainst(object: SceneObject, target: SceneObject, anchor: PositionAnchor, offset: number): Vec3 | null {
    const own = drawnBounds(object), on = drawnBounds(target);
    if (!own || !on) return null;
    const axis = ANCHOR_AXIS[anchor];
    const lo = [on.min.x, on.min.y, on.min.z], hi = [on.max.x, on.max.y, on.max.z];
    const olo = [own.min.x, own.min.y, own.min.z], ohi = [own.max.x, own.max.y, own.max.z];
    const shift = axis.map((dir, i) => dir > 0 ? hi[i] + offset - olo[i]
        : dir < 0 ? lo[i] - offset - ohi[i]
        : (lo[i] + hi[i]) / 2 - (olo[i] + ohi[i]) / 2);
    const world = object.transform.getAbsolutePosition().add(new Vector3(shift[0], shift[1], shift[2]));
    return worldToLocal(world, object.transform.parent as TransformNode | null);
}

// ── Definition ───────────────────────────────────────────────────────────────

export interface ToolDefinition {
    name: string;
    inputSchema: ToolInputSchema;
    verb: string;
    /** The tool changes the scene: a host that can leaves play mode first. */
    edits: boolean;
    /** An edit that only adds objects (MCP `destructiveHint: false`). */
    additive?: true;
    /** An edit that a second call with the same input leaves as the first left it (MCP `idempotentHint`). */
    idempotent?: true;
    /**
     * The tool reads an object's behaviors and writes them after an await: its
     * calls on one scene run one at a time with every other such tool's, so two
     * at once on one object cannot drop one's change.
     */
    serial?: true;
    /** The optional host members the tool cannot run without, checked before play mode is left so such a call changes nothing. */
    requires?: readonly Extract<keyof SceneToolHost, string>[];
    run(input: ToolInput, host: SceneToolHost): Promise<ToolOutcome>;
}

/**
 * A tool from its definition. A call is refused when the host lacks what the
 * tool requires, and an edit while an undo or redo replays; an edit leaves play
 * mode first when the host can. The input is read as fields, and an ambiguous
 * or deleted reference is answered.
 */
export function defineTool(definition: ToolDefinition): SceneTool {
    const { name, inputSchema, verb, edits, requires = [] } = definition;
    const serial = !!definition.serial;
    const call = async (input: unknown, host: SceneToolHost): Promise<ToolOutcome> => {
        if (requires.some(member => host[member] === undefined)) return fail(`Error: This editor does not support ${name}.`);
        if (edits && getUndoManager().isUndoRedoing) return fail(`Error: ${refusalText(host, UNDO_IN_PROGRESS_REFUSAL)}`);
        if (edits && host.leavePlayMode && host.scene.isPlayMode()) await host.leavePlayMode();
        try {
            return await definition.run(asInput(input), host);
        } catch (error) {
            if (error instanceof ObjectRefError) return fail(`Error: ${error.message}`);
            throw error;
        }
    };
    return {
        name,
        inputSchema,
        annotations: { readOnlyHint: !edits, destructiveHint: edits && !definition.additive, idempotentHint: !edits || !!definition.idempotent },
        meta: { runsOn: 'scene', edits, serial, requires, verb },
        run: (input, host) => serial ? oneAtATime(host.scene, () => call(input, host)) : call(input, host),
    };
}

const queues = new WeakMap<TraceScene, Promise<unknown>>();

/** Run `run` after every earlier call queued on `scene` has settled. */
function oneAtATime(scene: TraceScene, run: () => Promise<ToolOutcome>): Promise<ToolOutcome> {
    const next = (queues.get(scene) ?? Promise.resolve()).then(run);
    queues.set(scene, next.catch(() => undefined));
    return next;
}
